import { Permission, notEqual } from 'shared'
import { EditRowActionOptions } from 'components/CIMSTable/CIMSTableContext/types/actions/row'
import {
  DesignSubmittal,
  useDesignSubmittalLogContext,
} from '../../../../DesignSubmittalLogContext'
import { EditDesignSubmittalModalBody } from './components/EditDesignSubmittalModalBody'
import { usePayItemSelectContext } from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import { useEditDesignSubmittalSharePointReferenceMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { toMoment } from 'utils/toMoment'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import { EditDesignSubmittalModalValues } from './components/EditDesignSubmittalModalBody/types'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'

export function useEditOptions(): EditRowActionOptions<
  DesignSubmittal,
  EditDesignSubmittalModalValues
> {
  const { refetch, edit } = useDesignSubmittalLogContext()
  const { toLabeledValues: toPayItemLabeledValues } = usePayItemSelectContext()
  const { toCustomFieldEditorValue, toMutateCustomFieldsInput } =
    useProjectCustomFieldsContext()
  const [editSharePointReference] =
    useEditDesignSubmittalSharePointReferenceMutation({
      ...DefaultMutationHookOptions,
      onCompleted: () =>
        simpleSuccessNotification('SharePoint reference successfully edited'),
    })
  return {
    permission: Permission.EditDesignSubmittal,
    modal: {
      recordName: 'packageName',
      body: (record) => <EditDesignSubmittalModalBody {...record} />,
      initialValues: ({
        packageName,
        packageNumber,
        type,
        organization,
        requestedActionDate,
        contractDueDate,
        actions,
        dsToDSDisciplines,
        fieldDesignChanges,
        dsToPayItemNoGeometries,
        customFields,
        externalLinks,
      }) => ({
        packageName,
        packageNumber,
        type: {
          label: type.name,
          value: type.id,
          tooltip: type.description,
          deleted: type.deleted,
        },
        organization: {
          label: organization.name,
          value: organization.id,
          tooltip: organization.description,
          deleted: organization.deleted,
        },
        requestedActionDate: toMoment(requestedActionDate),
        contractDueDate: toMoment(contractDueDate),
        actions: toLabeledValues(actions, {
          label: 'name',
          value: 'id',
          deleted: ({ deleted }) => deleted,
          tooltip: 'description',
        }),
        disciplines: toLabeledValues(
          dsToDSDisciplines.map(({ discipline }) => discipline),
          {
            label: 'name',
            value: 'id',
            deleted: ({ deleted }) => deleted,
            tooltip: 'description',
          }
        ),
        payItems: toPayItemLabeledValues(
          dsToPayItemNoGeometries.map(({ payItem }) => payItem)
        ),
        fdcs: toLabeledValues(fieldDesignChanges, {
          label: 'formattedNumber',
          value: 'id',
          deleted: ({ deleted }) => deleted,
        }),
        externalLinks: externalLinks.map(({ __typename, ...link }) => link),
        customFields: toCustomFieldEditorValue(customFields),
      }),
      onOk: async (
        submittal,
        {
          packageName,
          packageNumber,
          type: { value: typeId },
          organization: { value: organizationId },
          requestedActionDate,
          contractDueDate,
          externalLinks,
          customFields,
          payItems,
          fdcs,
          actions,
          disciplines,
        }
      ) => {
        if (
          notEqual(packageName, submittal.packageName) ||
          notEqual(packageNumber, submittal.packageNumber)
        )
          await editSharePointReference({
            variables: {
              input: { id: submittal.id, packageName, packageNumber },
            },
          })

        await edit({
          id: submittal.id,
          typeId: typeId.toString(),
          organizationId: organizationId.toString(),
          actionIds: actions.map(({ value }) => value.toString()),
          disciplineIds: disciplines.map(({ value }) => value.toString()),
          payItemIds: payItems.map(({ value }) => value.toString()),
          fieldDesignChangeIds: fdcs.map(({ value }) => value.toString()),
          requestedActionDate: requestedActionDate?.toISOString() ?? null,
          contractDueDate: contractDueDate?.toISOString() ?? null,
          externalLinks,
          customFields: toMutateCustomFieldsInput(customFields),
        })
        refetch()
      },
    },
  }
}
